import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export interface BibRangePayload {
  event_id: string;
  distance: string;
  ticket_id?: string;
  start_number: number;
  end_number: number;
  sort_by: string;
  prefix?: string;
}

export interface BibRangeRecord extends BibRangePayload {
  id: string;
}

export interface ManualAssignPayload {
  registration_id: string;
  bib_number: number;
}

const extractRangeList = (data: unknown): BibRangeRecord[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as BibRangeRecord[];

  const root = data as Record<string, unknown>;
  if (Array.isArray(root.data)) return root.data as BibRangeRecord[];
  if (root.data && typeof root.data === "object") {
    const nested = root.data as Record<string, unknown>;
    if (Array.isArray(nested.data)) return nested.data as BibRangeRecord[];
  }

  return [];
};

/**
 * Fetch registrations with bib info for an event, filtered by distance and optionally ticket_id.
 * Response shape: { status, data: { data: Registration[] } }
 */
export const getList = (
  event_id: string,
  distance?: string,
  page = 1,
  limit = 10,
  ticket_id?: string,
) =>
  api.get("core/Order/Registration", {
    params: {
      event_id,
      with: ["order", "ticket"],
      limit,
      page,
      "filter[status][eq]": "placed",
      ...(distance ? { "filter[ticket.distance][eq]": distance } : {}),
      ...(ticket_id ? { "filter[ticket.id][eq]": ticket_id } : {}),
    },
  });

/**
 * List BIB ranges for an event (optionally filtered by distance).
 */
export const getRanges = (event_id: string, distance?: string) =>
  api.get("EventManagement/bib-range", {
    params: {
      event_id,
      ...(distance ? { distance } : {}),
    },
  });

/**
 * Create a BIB range + sorting policy for a distance.
 */
export const createRange = (payload: BibRangePayload) =>
  api.post("EventManagement/bib-range", payload);

/**
 * Update an existing BIB range (unique per event_id + distance).
 */
export const updateRange = (id: string, payload: BibRangePayload) =>
  api.put(`EventManagement/bib-range/${id}`, payload);

export const findRangeByDistance = async (
  event_id: string,
  distance: string,
): Promise<BibRangeRecord | null> => {
  const res = await getRanges(event_id, distance);
  const list = extractRangeList(res.data);
  return (
    list.find(
      (item) =>
        item.distance?.trim().toLowerCase() === distance.trim().toLowerCase(),
    ) ??
    list[0] ??
    null
  );
};

const isDuplicateRangeError = (err: unknown): boolean => {
  const errorText =
    (err as { response?: { data?: { error?: string } } })?.response?.data
      ?.error ?? "";
  return (
    errorText.includes("bib_ranges_event_id_distance_unique") ||
    errorText.includes("23505")
  );
};

/**
 * Create or update a BIB range for event_id + distance.
 */
export const saveRange = async (
  payload: BibRangePayload,
  existingId?: string,
) => {
  if (existingId) {
    return updateRange(existingId, payload);
  }

  const existing = await findRangeByDistance(payload.event_id, payload.distance);
  if (existing?.id) {
    return updateRange(existing.id, payload);
  }

  try {
    return await createRange(payload);
  } catch (err) {
    if (!isDuplicateRangeError(err)) throw err;
    const found = await findRangeByDistance(payload.event_id, payload.distance);
    if (!found?.id) throw err;
    return updateRange(found.id, payload);
  }
};

/**
 * Auto-assign BIB numbers for a specific distance / ticket.
 */
export const autoAssign = (
  event_id: string,
  distance: string,
  ticket_id?: string,
) =>
  api.post("EventManagement/bib/auto-assign", {
    event_id,
    distance,
    ...(ticket_id ? { ticket_id } : {}),
  });

/**
 * Manually assign or reassign a BIB number to a single registration.
 * Uses PUT when updating an existing BIB assignment.
 */
export const manualAssign = (registration_id: string, bib_number: number, isUpdate = false) =>
  isUpdate
    ? api.put("EventManagement/bib/manual-assign", { registration_id, bib_number })
    : api.post("EventManagement/bib/manual-assign", { registration_id, bib_number });

/**
 * Authorize all BIB assignments for a distance/ticket.
 */
export const authorizeAll = (
  event_id: string,
  distance: string,
  ticket_id?: string,
) =>
  api.post("EventManagement/bib/authorize", {
    event_id,
    distance,
    ...(ticket_id ? { ticket_id } : {}),
  });

const bibService = { getList, createRange, autoAssign, manualAssign, authorizeAll };
export default bibService;
